import { assertNever } from "@@/shared/assert-never";
import { type CurrencyCode } from "@@/shared/currencies/currencies";
import { type Price } from "@@/shared/prices/price";
import type { Except } from "type-fest";
import { currencySEK } from "./currency/currency-sek";

const currencyFormatter =
    (
        currency: CurrencyCode = "SEK",
        fractions: "AUTO" | "NONE" | "TWO" = "AUTO",
        symbol = "kr",
    ) =>
    (value: number) => {
        const hasFractions = value % 1 !== 0;

        if (currency !== "SEK")
            throw new Error("Currency not supported; " + currency);
        switch (fractions) {
            case "NONE":
                return currencySEK(value, symbol).format({ precision: 0 });
            case "TWO":
                return currencySEK(value, symbol).format({ precision: 2 });
            case "AUTO":
            default:
                return currencySEK(value, symbol).format({
                    precision: hasFractions ? 2 : 0,
                });
        }
    };

type AmountType =
    | "AMOUNT_INCLUDING_VAT"
    | "AMOUNT_EXCLUDING_VAT"
    | "VAT_AMOUNT";

const getAmount = (price: Price, amountType: AmountType) => {
    if (amountType === "AMOUNT_INCLUDING_VAT") return price.amountIncludingVat;
    if (amountType === "AMOUNT_EXCLUDING_VAT") return price.amountExcludingVat;
    if (amountType === "VAT_AMOUNT") return price.vatAmount;
    assertNever(amountType, "Unrecognized amount type");
};
type FractionType = "AUTO" | "NONE" | "TWO";
type PriceFormatterParams = {
    price: Price | undefined;
    currency: CurrencyCode;
    /**
     * E.g. "från"
     * @type {string}
     */
    prefix?: string;
    /**
     * defaults to `AMOUNT_INCLUDING_VAT`
     * @type {AmountType}
     */
    amountType?: AmountType;
    /**
     * defaults to `NONE`
     * @type {FractionType}
     */
    fractions?: FractionType;
    /**
     * defaults to `kr`, but if you wan't to translate it
     * provide the current translation for it here
     * @type {string}
     */
    symbol?: string;
};
const priceFormatter = (params: PriceFormatterParams) => {
    const { price, currency } = params;
    if (typeof price === "undefined") return "";
    const prefix = params.prefix?.trim();
    const fractions = params.fractions ?? "NONE";
    const formattedPrefix =
        (prefix && (prefix + " ").replace(/\s\s+/g, " ")) || "";

    // TODO! CONVERT TO OTHER CURRENCIES
    // TODO! ALL PRICES ARE IN SEK
    if (currency !== "SEK")
        throw new Error("Currency not supported; " + currency);

    const value = getAmount(price, params.amountType ?? "AMOUNT_INCLUDING_VAT");

    const output =
        formattedPrefix +
        currencySEK(value, params.symbol).format({
            precision: fractions === "NONE" ? 0 : 2,
        });

    return fractions !== "AUTO"
        ? output
        : output.includes(",00")
          ? output.replace(",00", "")
          : output;
};

const formatSEK = (params: Except<PriceFormatterParams, "currency">) =>
    priceFormatter({ currency: "SEK", ...params });
const formatSEKNumber = (value: number, symbol = "kr") => {
    const output = currencySEK(value, symbol).format({
        precision: 2,
    });
    return output;
};

const priceFormatters = {
    formatSEK,
};

export {
    currencyFormatter,
    formatSEK,
    formatSEKNumber,
    priceFormatter,
    priceFormatters,
};
export type { AmountType };
